<template>
  <BaseTemplate app-name="Perfil" page-title="">
    <template #content>
      <div class="container mx-auto px-4 py-8 max-w-5xl">
        <!-- Avatar Section -->
        <div class="flex justify-center mb-8">
          <div class="avatar-container">
            <div class="avatar-wrapper">
              <!-- Enquanto loadingProfile for true, exibe spinner; caso contrário, a imagem -->
              <template v-if="loadingProfile">
                <div class="spinner"></div>
              </template>
              <template v-else>
                <img
                  :src="profileImageComputed"
                  alt="Avatar"
                  class="avatar-image"
                  @error="handleImageError"
                />
              </template>
            </div>
            <button
              @click="triggerFileInput"
              class="avatar-edit-button"
              aria-label="Alterar imagem de perfil"
            >
              <Camera class="w-5 h-5 sm:w-10 sm:h-10" />
            </button>
            <input
              type="file"
              ref="fileInputRef"
              @change="handleImageUpload"
              accept="image/*"
              class="hidden"
            />
          </div>
        </div>

        <!-- Profile Information Card -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          <div class="p-6 md:p-8">
            <!-- Name Section -->
            <div class="mb-8">
              <div v-if="isEditingName" class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <input
                  v-model="firstName"
                  class="w-full sm:flex-1 border-b-2 border-blue-500 px-2 py-1 text-xl md:text-2xl font-semibold focus:outline-none bg-transparent dark:text-white"
                  placeholder="Nome"
                />
                <input
                  v-model="lastName"
                  class="w-full sm:flex-1 border-b-2 border-blue-500 px-2 py-1 text-xl md:text-2xl font-semibold focus:outline-none mt-4 sm:mt-0 bg-transparent dark:text-white"
                  placeholder="Sobrenome"
                />
                <div class="flex gap-2 mt-4 sm:mt-0">
                  <button @click="cancelEditingName" class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-2 transition-colors">
                    <X class="w-5 h-5" />
                  </button>
                  <button @click="saveName" class="clinic-btn px-4 py-2 rounded-md transition-colors">
                    Salvar
                  </button>
                </div>
              </div>
              <div v-else class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                    {{ `${firstName} ${lastName}` }}
                  </h2>
                </div>
                <button @click="startEditingName" class="clinic-text hover:text-blue-600 text-sm flex items-center gap-2 mt-2 sm:mt-0 transition-colors">
                  <Edit class="w-4 h-4 clinic-icon" /> Editar
                </button>
              </div>
            </div>

            <!-- Profile Information -->
            <div class="space-y-6">
              <div class="space-y-2">
                <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300">E-mail:</label>
                <div class="flex items-center gap-3 clinic-border border-b border-gray-300 dark:border-gray-600 focus-within:border-blue-500 transition-colors">
                  <Mail class="w-5 h-5 clinic-icon" />
                  <input 
                    id="email" 
                    v-model="email" 
                    class="w-full px-2 py-2 focus:outline-none bg-transparent dark:text-white"
                  />
                </div>
              </div>

              <div class="flex justify-end mt-8">
                <button 
                  @click="saveChanges" 
                  :disabled="isSaving"
                  class="clinic-btn px-6 py-3 rounded-lg shadow-md transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="isSaving">Salvando...</span>
                  <span v-else>Salvar Alterações</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- API Key Card (Separado) -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden mt-6">
          <div class="p-6 md:p-8">
            <h3 class="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Sua API Key</h3>
            <div class="space-y-2">
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Esta é sua chave de API pessoal. Você pode visualizá-la ou copiá-la para usar em suas integrações.
              </p>
              <div class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <div class="flex-1 font-mono overflow-hidden overflow-ellipsis whitespace-nowrap text-gray-500 hover:text-gray-700">
                  <span v-if="showApiKey">{{ apiKey }}</span>
                  <span v-else>{{ apiKey.replace(/./g, '•') }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <button 
                    @click="toggleApiKey" 
                    class="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none transition-colors"
                    :title="showApiKey ? 'Ocultar API Key' : 'Mostrar API Key'"
                  >
                    <EyeOff v-if="showApiKey" class="w-5 h-5" />
                    <Eye v-else class="w-5 h-5" />
                  </button>
                  <button 
                    @click="copyApiKey" 
                    class="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none transition-colors"
                    title="Copiar API Key"
                  >
                    <Copy class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- InovaScan Download Card -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden mt-6">
          <div class="p-6 md:p-8">
            <div class="flex items-start">
              <div class="flex-1">
                <img 
                  src="@/assets/inovascan.png" 
                  alt="InovaScan Logo" 
                  class="w-40 h-16 object-contain mb-2"
                />
                <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Baixe o executável do InovaScan para digitalizar documentos e imagens diretamente no sistema.
                </p>
                <div class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div>
                    <p class="font-medium text-gray-900 dark:text-white">Instalador InovaScan</p>
                  </div>
                  <button 
                    @click="downloadInovaScan" 
                    :disabled="downloadingInovaScan"
                    class="clinic-btn px-4 py-2 rounded-md transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Baixar InovaScan"
                  >
                    <Download class="w-5 h-5" />
                    <span v-if="downloadingInovaScan">Baixando...</span>
                    <span v-else>Baixar</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal de Recorte de Imagem -->
        <div v-if="showCropper" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl">
            <h3 class="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Ajustar Imagem de Perfil</h3>
            <div class="flex justify-center mb-4">
              <div class="relative rounded-full overflow-hidden border-4 border-white dark:border-gray-700 shadow-lg" style="width: 300px; height: 300px;">
                <cropper
                  ref="cropper"
                  :src="imageToCrop"
                  :stencil-component="circleStencil"
                  :stencil-props="{
                    aspectRatio: 1,
                    movable: true,
                    resizable: true,
                    handlers: {
                      eastNorth: true,
                      westNorth: true,
                      eastSouth: true,
                      westSouth: true
                    }
                  }"
                  :canvas="{ width: 300, height: 300 }"
                  class="cropper"
                />
              </div>
            </div>
            <div class="flex justify-end space-x-3">
              <button 
                @click="cancelCrop" 
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button 
                @click="cropAndSaveImage" 
                class="clinic-btn px-4 py-2 rounded-md transition-colors"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>

        <!-- Toast de Notificação -->
        <transition name="toast">
          <div v-if="showToast" class="fixed bottom-4 right-4 z-50 max-w-md w-full md:w-auto">
            <div 
              class="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden flex items-center p-4 border-l-4"
              :class="{
                'border-green-500': toastType === 'success',
                'border-red-500': toastType === 'error',
                'border-yellow-500': toastType === 'warning',
                'border-blue-500': toastType === 'info'
              }"
            >
              <div class="flex-shrink-0 mr-3">
                <CheckCircle v-if="toastType === 'success'" class="w-6 h-6 text-green-500" />
                <AlertCircle v-else-if="toastType === 'error'" class="w-6 h-6 text-red-500" />
                <AlertTriangle v-else-if="toastType === 'warning'" class="w-6 h-6 text-yellow-500" />
                <Info v-else class="w-6 h-6 text-blue-500" />
              </div>
              <div class="flex-1">
                <p class="font-medium text-gray-900 dark:text-white">{{ toastMessage }}</p>
              </div>
              <button @click="closeToast" class="ml-4 text-gray-400 hover:text-gray-500 focus:outline-none">
                <X class="w-5 h-5" />
              </button>
            </div>
          </div>
        </transition>

        <p class="text-center text-sm mt-10 text-gray-500 dark:text-gray-400">
          Desenvolvido por Tecnologia da Informação - CCDTI © Todos os direitos reservados
        </p>
      </div>
    </template>
  </BaseTemplate>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper'
import { CircleStencil } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import apiClient from '@/services/authService'
import { Camera, Mail, X, Edit, Eye, EyeOff, Copy, CheckCircle, AlertCircle, AlertTriangle, Info, Download } from 'lucide-vue-next'
import BaseTemplate from '../templates/BaseTemplate.vue'
import { usePerfilStore } from '@/stores/settings/perfilStore'
import { useClinicStore } from '@/stores/settings/clinicStore'

export default {
  components: {
    Cropper,
    Camera,
    Mail,
    X,
    Edit,
    Eye,
    EyeOff,
    Copy,
    CheckCircle,
    AlertCircle,
    AlertTriangle,
    Info,
    Download,
    BaseTemplate
  },
  data() {
    return {
      circleStencil: CircleStencil,
      firstName: '',
      lastName: '',
      email: '',
      profileImage: '',
      isEditingName: false,
      showCropper: false,
      imageToCrop: '',
      fileInputRef: null,
      loadingProfile: true,
      apiKey: '',
      showApiKey: false,
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      toastTimeout: null,
      clinicStore: null,
      isSaving: false, // Controla se está salvando para evitar múltiplas chamadas
      downloadingInovaScan: false, // Controla se está baixando o InovaScan
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInputRef.click()
    },
    handleImageUpload(event) {
      const file = event.target.files?.[0]
      if (file) {
        if (file.size > 1024 * 1024 * 5) {
          this.showToastMessage("Imagem muito grande. Máximo 5MB.", "error")
          return
        }
        const reader = new FileReader()
        reader.onload = (e) => {
          if (e.target?.result) {
            this.imageToCrop = e.target.result
            this.showCropper = true
          }
        }
        reader.readAsDataURL(file)
      }
    },
    copyApiKey() {
      if (!this.apiKey) return
      navigator.clipboard.writeText(this.apiKey).then(() => {
        this.showToastMessage("API Key copiada com sucesso!")
      }).catch(err => {
        console.error("Erro ao copiar API Key:", err)
        this.showToastMessage("Erro ao copiar API Key.", "error")
      })
    },
    showToastMessage(message, type = 'success') {
      // Limpa qualquer timeout existente
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout)
      }
      
      // Define a mensagem, o tipo e mostra o toast
      this.toastMessage = message
      this.toastType = type // Agora estamos usando o parâmetro type
      this.showToast = true
      
      // Define um timeout para fechar o toast automaticamente após 3 segundos
      this.toastTimeout = setTimeout(() => {
        this.showToast = false
      }, 3000)
    },
    closeToast() {
      this.showToast = false
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout)
      }
    },
    cropAndSaveImage() {
      const cropper = this.$refs.cropper
      if (cropper) {
        try {
          const result = cropper.getResult({
            size: { width: 300, height: 300 },
            fillColor: 'rgba(0, 0, 0, 0)'
          })
          if (result && result.canvas) {
            // Converte para JPEG para melhor compatibilidade
            this.profileImage = result.canvas.toDataURL('image/jpeg', 0.8)
            console.log('Imagem processada:', this.profileImage.substring(0, 50) + '...')
            this.showCropper = false
            this.saveChanges()
          }
        } catch (error) {
          console.error("Erro ao processar imagem:", error)
          this.showToastMessage("Erro ao processar imagem.", "error")
        }
      }
    },
    cancelCrop() {
      this.showCropper = false
    },
    startEditingName() {
      this.isEditingName = true
    },
    cancelEditingName() {
      this.isEditingName = false
    },
    saveName() {
      this.isEditingName = false
    },
    toggleApiKey() {
      this.showApiKey = !this.showApiKey
    },
    async carregarPerfil() {
      this.loadingProfile = true
      try {
        const { data } = await apiClient.get('/api/v1/profile/')
        this.firstName = data.nome
        this.lastName = data.sobrenome
        this.email = data.email
        this.apiKey = data.api_key
        
        // Trata a imagem de perfil
        if (data.imagem_base64) {
          if (data.imagem_base64.startsWith('http')) {
            this.profileImage = data.imagem_base64
          } else if (data.imagem_base64.startsWith('data:image')) {
            this.profileImage = data.imagem_base64
          } else {
            // Assume que é base64 puro
            this.profileImage = `data:image/jpeg;base64,${data.imagem_base64}`
          }
        } else {
          this.profileImage = '/default-avatar.png'
        }
      } catch (error) {
        console.error("Erro ao carregar perfil:", error)
        this.showToastMessage("Erro ao carregar perfil.", "error")
        this.profileImage = '/default-avatar.png'
      } finally {
        this.loadingProfile = false
      }
    },
    async saveChanges() {
      if (this.isSaving) return // Evita múltiplas chamadas simultâneas
      
      this.isSaving = true
      try {
        const payload = {
          nome: this.firstName,
          sobrenome: this.lastName,
          email: this.email,
          // Envia a imagem somente se for nova; caso contrário, envia string vazia para evitar erro
          imagem_base64: this.profileImage.startsWith('data:image')
            ? this.profileImage.split(',')[1]
            : ''
        }
        await apiClient.put('/api/v1/profile/', payload)
        this.showToastMessage("Perfil atualizado com sucesso!")
        
        // Atualiza o store sem fazer nova requisição
        const perfilStore = usePerfilStore()
        perfilStore.firstName = this.firstName
        perfilStore.lastName = this.lastName
        perfilStore.email = this.email
        perfilStore.profileImage = this.profileImage
        perfilStore.carregado = true
      } catch (error) {
        console.error("Erro ao salvar alterações:", error)
        this.showToastMessage("Erro ao salvar perfil.", "error")
      } finally {
        this.isSaving = false
      }
    },
    handleImageError() {
      // Se a imagem falhar ao carregar, usa a imagem padrão
      this.profileImage = '/default-avatar.png'
      console.warn("Erro ao carregar imagem de perfil, usando imagem padrão")
    },
    async downloadInovaScan() {
      if (this.downloadingInovaScan) return // Evita múltiplas chamadas simultâneas
      
      this.downloadingInovaScan = true
      try {
        // Obter URL de download do backend
        const response = await apiClient.get('/api/v1/auth/download-inovascan/')
        
        if (response.data && response.data.download_url) {
          // Criar link temporário para download
          const link = document.createElement('a')
          link.href = response.data.download_url
          link.download = response.data.file_name
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          this.showToastMessage("Download do InovaScan iniciado com sucesso!")
        } else {
          throw new Error("URL de download não encontrada")
        }
      } catch (error) {
        console.error("Erro ao baixar InovaScan:", error)
        this.showToastMessage("Erro ao baixar InovaScan. Tente novamente.", "error")
      } finally {
        this.downloadingInovaScan = false
      }
    }
  },
  computed: {
    // Computed para a imagem de perfil: se o perfil estiver carregado e a imagem for válida, retorna-a; caso contrário, fallback
    profileImageComputed() {
      if (!this.profileImage) {
        return '/default-avatar.png'
      }
      
      if (this.profileImage.startsWith('data:image') || this.profileImage.startsWith('http')) {
        return this.profileImage
      }
      
      // Se for base64 puro, adiciona o prefixo
      if (this.profileImage && !this.profileImage.includes('data:image') && !this.profileImage.startsWith('http')) {
        return `data:image/jpeg;base64,${this.profileImage}`
      }
      
      return '/default-avatar.png'
    }
  },
  mounted() {
    this.carregarPerfil()
    this.fileInputRef = this.$refs.fileInputRef
    this.clinicStore = useClinicStore()
    this.clinicStore.initialize()
  },
  beforeUnmount() {
    // Limpa o timeout se o componente for desmontado
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout)
    }
  }
}
</script>

<style scoped>
/* Avatar Section */
.avatar-container {
  position: relative;
  display: inline-block;
}

.avatar-wrapper {
  width: 300px;
  height: 300px;
  border-radius: 50%;
  border: 4px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  background-color: white;
}

.dark .avatar-wrapper {
  border-color: #1f2937;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.avatar-edit-button {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: var(--clinic-btn-bg);
  color: white;
  padding: 10px;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.avatar-edit-button:hover {
  background-color: var(--clinic-btn-bg-hover);
  transform: scale(1.1);
}

/* Cropper Styles */
.cropper {
  width: 100%;
  height: 100%;
  background: #ddd;
}

.cropper__image {
  object-fit: contain;
}

.cropper__stencil {
  overflow: hidden;
  border-radius: 50%;
}

/* Toast Animation */
.toast-enter-active, .toast-leave-active {
  transition: all 0.3s ease;
}
.toast-enter-from, .toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

@media (max-width: 640px) {
  .avatar-wrapper {
    width: 250px;
    height: 250px;
  }
}

@media (max-width: 400px) {
  .avatar-wrapper {
    width: 200px;
    height: 200px;
  }
}

.spinner {
  border: 10px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #3b82f6; /* cor do spinner, conforme seu tema */
  border-radius: 50%;
  width: 100%;
  height: 100%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.clinic-btn {
  background: var(--clinic-btn-bg) !important;
  color: #fff !important;
  transition: background 0.2s;
}

.clinic-btn:hover {
  background: var(--clinic-btn-bg-hover) !important;
}

.clinic-border {
  border-color: var(--clinic-btn-bg-hover) !important;
}

.clinic-text {
  color: var(--clinic-font-color) !important;
}

.clinic-icon {
  color: var(--clinic-icon-color) !important;
}
</style>